import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ErrorService } from './error.service';
import { ErrorHandlerInputs } from '../models/errors.models';
import { ERROR_HANDLER_TOKEN } from '../tokens/errors.token';

/**
 * Service to handle error management in a reusable and consistent way.
 * Provides utilities to handle errors in observable streams, clear existing errors, and set new error messages.
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  /**
   * Injects the `ErrorService` and `APP_ERROR_HANDLER_TOKEN` to manage error states and messages across the application.
   */
  protected readonly errorService = inject(ErrorService);
  private readonly appErrorHandler = inject(ERROR_HANDLER_TOKEN);

  /**
   * Returns a higher-order function that applies error handling logic to an observable stream.
   * Clears any existing error for the given key before subscribing and sets a new error if the stream encounters one.
   *
   * @template T - The type of data emitted by the observable stream.
   * @param inputs - An object containing the error key and fallback value.
   * @returns A function that takes an observable stream, applies error handling, and returns the modified stream.
   */
  public handleError<T>({
    errorKey,
    fallbackValue,
  }: Omit<ErrorHandlerInputs<T>, 'errorMessage'>): (
    source$: Observable<T>,
  ) => Observable<T> {
    const errors = this.appErrorHandler.errors;
    const errorMessage = errors[errorKey];
    const isDebug = this.appErrorHandler.debug;

    return (source$: Observable<T>): Observable<T> =>
      source$.pipe(
        tap(() => this.errorService.clearError(errorKey)),
        catchError((error) => {
          if(isDebug) console.error(errorMessage, error);
          this.errorService.setError(errorKey, errorMessage);
          return of(fallbackValue);
        }),
      );
  }
}
